//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSelector } from '@reduxjs/toolkit';
import { ConstantsState, Customer } from '../slices/slice_types';
import { RootState } from '@/redux/store';

const selectDataTable = (state: RootState) => state.dataTable;
const selectCurrentTableKey = createSelector([selectDataTable], (dataTable) => dataTable.currentTable || null);
const selectCurrentTable = createSelector(
  [selectDataTable, selectCurrentTableKey],
  (dataTable, currentTableKey) => dataTable[currentTableKey as keyof typeof dataTable] || {}
);
const selectConstants = (state: RootState) => state.constants || {};
const selectTaskStatuses = createSelector([selectConstants], (constants) => constants.tasks?.statuses || {});
export const selectIngestFileTypes = createSelector([selectConstants], (constants) => constants.fileIngest?.file_types || {});
const selectOrderConstants = createSelector([selectConstants], (constants) => constants.orders || {});
const selectTitleTypes = createSelector([selectConstants], (constants) => constants.titles?.types || {});
const selectRawCustomerList = createSelector([selectOrderConstants], (orderConstants) => orderConstants.customerIDNames || {});
const selectFileTypeGroupings = createSelector([selectConstants], (constants) => constants?.commonConstants?.fileTypeGrouping || {});
const selectExceptionStatuses = createSelector([selectConstants], (constants) => constants.exceptions?.statuses || {});
export const selectBillableCustomers = createSelector(
  [selectConstants],
  (constants) => constants.orders?.billableCustomers || []
);

export const selectNonBillableCustomers = createSelector(
  [selectConstants],
  (constants) => constants.orders?.nonBillableCustomers || []
);

const selectStatuses = createSelector([selectCurrentTableKey, selectConstants], (currentTableKey, constants) => {
  if (!currentTableKey) return {};

  if (currentTableKey?.toLowerCase().includes('tasks')) {
    return constants.tasks?.statuses || {};
  } else {
    return constants[currentTableKey as keyof ConstantsState]?.statuses || {};
  }
});

const selectDataTableParams = createSelector(selectCurrentTable, (settings) => settings.params || {});

export const selectEntityTypesFilter = createSelector([selectDataTableParams, selectTitleTypes], (params, types) => {
  const { entity_types_filter } = params;
  const entityTypesFilter: { [key: string]: any } = {};
  let entityTypes = ['feature', 'series', 'episode'];
  if (entity_types_filter?.length) {
    entityTypes = entity_types_filter;
  }

  entityTypes.forEach((type) => {
    entityTypesFilter[type] = types[type];
  });

  return entityTypesFilter;
});

export const selectStatusFilter = createSelector(
  [selectCurrentTableKey, selectOrderConstants, selectTaskStatuses, selectExceptionStatuses, selectStatuses],
  (key, orderConstants, taskStatuses, exceptionStatuses, statuses) => {
    if (key === 'orders') {
      const { statuses } = orderConstants;
      return statuses;
    } else if (key === 'tasks') {
      return taskStatuses;
    } else if (key === 'exceptions') {
      return exceptionStatuses;
    } else {
      return statuses;
    }
  }
);

export const selectCustomerFilter = createSelector(
  [selectCurrentTableKey, selectOrderConstants, selectRawCustomerList],
  (key, orderConstants, rawCustomerList): Record<string, any> => {
    if (key === 'profiles') {
      const { customers } = orderConstants;
      return customers || {};
    } else {
      return rawCustomerList;
    }
  }
);

export const selectFileTypeFilter = createSelector(
  [selectCurrentTableKey, selectIngestFileTypes, selectFileTypeGroupings],
  (key, ingestFileTypes, fileTypeGroupings) => {
    const fileTypes: any = {};

    if (key === 'fileIngest' && Object.keys(ingestFileTypes).length) return ingestFileTypes;

    // capitalize first letter of key
    Object.keys(fileTypeGroupings).forEach((key) => {
      if (key !== 'all') {
        fileTypes[key] = key.charAt(0).toUpperCase() + key.slice(1);
      }
    });

    return fileTypes;
  }
);

export const selectBillableCustomersFilter = createSelector(selectBillableCustomers, (customers) => {
  const map: { [key: string]: string } = {};

  customers.forEach((customer: Customer) => (map[customer.id] = customer.name));

  return map;
});

export const selectNonBillableCustomersFilter = createSelector(selectNonBillableCustomers, (customers) => {
  const map: { [key: string]: string } = {};

  customers.forEach((customer: Customer) => (map[customer.id] = customer.name));

  return map;
});

export const selectContentTypes = createSelector([selectCurrentTableKey, selectConstants], (currentTableKey, constants) => {
  if (!currentTableKey) return {};
  return constants.pendingAssets?.contentTypes || {};
});
